import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { token } from './session';
import { getPage, getObject, type GetPath, type Row, type Schema } from './client';
import { canAccessTargets } from '../hr-permissions';
import { listQuery, supportsListSearch } from './list-query';
import {
  permissionAlternatives,
  type AccessPrincipal,
  type AccessTarget,
  type PermissionCode,
} from '@snaphire/contracts';

export function useRows<T extends Row = Row>(
  path: GetPath,
  query: Record<string, string | number | boolean | undefined> = {},
  options: {
    params?: Record<string, string>;
    enabled?: boolean;
    refetchInterval?: number | false;
  } = {},
) {
  const serverQuery = listQuery(path, { limit: 100, ...query });
  const result = useInfiniteQuery({
    queryKey: [path, serverQuery, options.params],
    enabled: options.enabled ?? true,
    refetchInterval: options.refetchInterval,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      getPage<T>(path, { ...serverQuery, cursor: pageParam }, signal, options.params),
    getNextPageParam: (page) => (page.page.hasMore ? page.page.nextCursor || undefined : undefined),
  });
  const completeSearch = Boolean(query.search) && !supportsListSearch(path);
  const { hasNextPage, isFetchingNextPage, isError, fetchNextPage } = result;
  useEffect(() => {
    if (completeSearch && hasNextPage && !isFetchingNextPage && !isError) void fetchNextPage();
  }, [completeSearch, hasNextPage, isFetchingNextPage, isError, fetchNextPage]);
  return { ...result, rows: result.data?.pages.flatMap((page) => page.items) || [] };
}
export function useAccess() {
  const result = useQuery({
    queryKey: ['access'],
    enabled: Boolean(token()),
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    queryFn: ({ signal }) => getObject<Schema<'CurrentUserResponse'>>('/api/v1/me', {}, signal),
  });
  return {
    ...result,
    can: (permission: string, target?: Partial<AccessTarget> | readonly Partial<AccessTarget>[]) =>
      Boolean(
        target && result.data
          ? canAccessTargets(
              {
                workspaceId: result.data.account.workspaceId,
                accountId: result.data.account.id,
                workAreas: result.data.workAreas,
                grants: result.data.grants,
                overrides: result.data.overrides,
              } as AccessPrincipal,
              permission as PermissionCode,
              Array.isArray(target) ? target : [target as Partial<AccessTarget>],
            )
          : !result.data?.overrides.some(
              (override) =>
                permissionAlternatives(permission as PermissionCode).includes(
                  override.permission as PermissionCode,
                ) &&
                override.effect === 'DENY' &&
                override.scope === 'WORKSPACE' &&
                !override.targetEntityId,
            ) &&
              (result.data?.grants.some((grant) =>
                permissionAlternatives(permission as PermissionCode).includes(
                  grant.permission as PermissionCode,
                ),
              ) ||
                result.data?.overrides.some(
                  (override) =>
                    permissionAlternatives(permission as PermissionCode).includes(
                      override.permission as PermissionCode,
                    ) && override.effect === 'ALLOW',
                )),
      ),
  };
}
export async function allRows<T extends Row = Row>(
  path: GetPath,
  params: Record<string, string> = {},
  signal?: AbortSignal,
): Promise<T[]> {
  const rows: T[] = [];
  let cursor: string | undefined;
  do {
    const page = await getPage<T>(path, { limit: 100, cursor }, signal, params);
    rows.push(...page.items);
    if (!page.page.hasMore) break;
    if (!page.page.nextCursor || page.page.nextCursor === cursor)
      throw new Error('Не удалось загрузить следующую страницу записей');
    cursor = page.page.nextCursor;
  } while (!signal?.aborted);
  return rows;
}
export function useCompleteRows<T extends Row = Row>(path: GetPath, enabled = true) {
  const result = useQuery({
    queryKey: ['all-rows', path],
    enabled,
    queryFn: ({ signal }) => allRows<T>(path, {}, signal),
  });
  return { ...result, rows: result.data || [] };
}
export function useEmployee(id: string | undefined) {
  return useQuery({
    queryKey: ['employee', id],
    enabled: Boolean(id),
    queryFn: ({ signal }) =>
      getObject<Schema<'EmployeeResponse'>>(
        '/api/v1/employees/{employeeId}',
        { employeeId: id! },
        signal,
      ),
  });
}
export function useWorkspace() {
  return useQuery({
    queryKey: ['workspace'],
    queryFn: ({ signal }) =>
      getObject<Schema<'WorkspaceResponse'>>('/api/v1/workspace', {}, signal),
  });
}
